<template>
    <div class="px-2 mb-20 w-full">
        <div id="title">
            <p class="text-black dark:text-white text-justify">
                {{ $t("indexPage.description") }}
                {{ $t("indexPage.introText") }}
            </p>
            <div class="flex flex-col md:flex-row justify-center mt-4">
                <a
                    href="#json-formatter"
                    class="no-underline hover:underline text-primary-primary mx-2"
                >
                    {{ $t("indexPage.jsonFormatter") }}
                </a>
                <a
                    href="#json-serializer"
                    class="no-underline hover:underline text-primary-primary mx-2"
                >
                    {{ $t("indexPage.jsonSerializer") }}
                </a>
                <a
                    href="#json-deserializer"
                    class="no-underline hover:underline text-primary-primary mx-2"
                >
                    {{ $t("indexPage.jsonDeserializer") }}
                </a>
                <a
                    href="#whats-different"
                    class="no-underline hover:underline text-primary-primary mx-2"
                >
                    {{ $t("indexPage.whatsDifferent") }}
                </a>
                <change-theme class="mx-2" />
            </div>
        </div>
        <div id="json-formatter" class="mt-8 pt-4">
            <h2 class="text-xl font-semibold dark:text-white">
                {{ $t("indexPage.jsonFormatter") }}
            </h2>
            <div class="mt-4">
                <p class="text-black dark:text-white text-justify">
                    {{ $t("indexPage.pasteAndFormat") }}
                </p>
            </div>
            <json-formatter class="mt-4" />
        </div>
        <div id="json-serializer" class="mt-8 pt-4">
            <h2 class="text-xl font-semibold dark:text-white">
                {{ $t("indexPage.jsonSerializer") }}
            </h2>
            <div class="mt-4">
                <p class="text-black dark:text-white text-justify">
                    {{ $t("indexPage.transformExample") }}
                </p>
                <pre
                    class="mt-3 font-mono bg-gray-700 text-green-400 p-4 rounded mb-2"
                >
{
    "amount": 20000
}</pre
                >
                <p class="text-black dark:text-white text-justify">
                    {{ $t("indexPage.inTextString") }}
                    <in-line-code-viewer>
                        "{\"amount\":20000}" </in-line-code-viewer
                    >. {{ $t("indexPage.pasteInstructions") }}
                </p>
            </div>
            <json-serializer class="mt-4" />
        </div>
        <div id="json-deserializer" class="mt-8 pt-4">
            <h2 class="text-xl font-semibold dark:text-white">
                {{ $t("indexPage.jsonDeserializer") }}
            </h2>
            <div class="mt-4">
                <p class="text-black dark:text-white text-justify">
                    {{ $t("indexPage.transformSerializedJson") }}
                    <in-line-code-viewer>
                        "{\"amount\":20000}"
                    </in-line-code-viewer>
                    {{ $t("indexPage.orCompactJson") }}
                    <in-line-code-viewer>
                        {"amount":20000}
                    </in-line-code-viewer>
                    {{ $t("indexPage.intoFormatted") }}
                </p>
                <pre
                    class="mt-3 font-mono bg-gray-700 text-green-400 p-4 rounded mb-2"
                >
{
    "amount": 20000
}</pre
                >
                <p class="text-black dark:text-white text-justify">
                    {{ $t("indexPage.pasteAndProcess") }}
                </p>
            </div>
            <json-deserializer class="mt-4" />
        </div>

        <div id="whats-different" class="mt-8 pt-4">
            <h2 class="text-xl font-semibold dark:text-white">
                {{ $t("indexPage.whatsDifferent") }}
            </h2>
            <ul class="list-disc pl-6 pr-2 my-6">
                <li class="mb-4">
                    <p class="text-justify">
                        {{ $t("indexPage.feature1") }}
                    </p>
                </li>
                <li class="mb-4">
                    <p class="text-justify">
                        {{ $t("indexPage.feature2") }}
                        <in-line-code-viewer>"</in-line-code-viewer> a
                        <in-line-code-viewer>/"</in-line-code-viewer>
                        {{ $t("indexPage.characters") }}
                    </p>
                </li>
            </ul>
        </div>
        <div id="whats-is-json" class="mt-8 pt-4">
            <h2 class="text-xl font-semibold dark:text-white">
                {{ $t("indexPage.whatIsJson") }}
            </h2>
            <div class="my-6">
                <p>
                    <strong>{{ $t("indexPage.serialization") }}</strong>
                    {{ $t("indexPage.serializationDesc") }}
                </p>
                <br />
                <p>
                    <strong>{{ $t("indexPage.deserialization") }}</strong>
                    {{ $t("indexPage.deserializationDesc") }}
                </p>
            </div>
            <h3 class="text-xl font-semibold dark:text-white mt-3">
                {{ $t("indexPage.utilityAndStorage") }}
            </h3>
            <div class="my-6">
                <p>
                    {{ $t("indexPage.utilityDesc") }}
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { reactive, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import { useMeta } from "vue-meta";

import InLineCodeViewer from "@/components/json/InLineCodeViewer.vue";
import JsonDeserializer from "@/components/json/JsonDeserializer.vue";
import JsonFormatter from "@/components/json/JsonFormatter.vue";
import JsonSerializer from "@/components/json/JsonSerializer.vue";
import ChangeTheme from "@/components/utils/ChangeTheme.vue";

export default {
    name: "IndexPage",
    components: {
        ChangeTheme,
        JsonSerializer,
        JsonFormatter,
        JsonDeserializer,
        InLineCodeViewer
    },
    setup() {
        const { t } = useI18n();

        useMeta({
            title: t("indexPage.meta.title"),
            meta: [
                {
                    name: "description",
                    content: t("indexPage.meta.description")
                },
                {
                    name: "keywords",
                    content: t("indexPage.meta.keywords")
                },
                {
                    name: "og:image:secure_url",
                    content: `/img/index.jpg`
                },
                { name: "og:image:height", content: "627" },
                { name: "og:image:width", content: "1200" },
                {
                    name: "og:image:alt",
                    content: t("indexPage.meta.ogImageAlt")
                },
                { name: "og:image:type", content: "image/jpg" }
            ],
            htmlAttrs: { lang: "en", amp: true }
        });
        const state = reactive({
            jsonInstances: [{ id: 1, name: "Default" }],
            datesInstances: [{ id: 1, name: "", json: 0 }]
        });
        const newJSON = () => {
            const lastJSON = state.jsonInstances.at(-1);
            state.jsonInstances.push({
                id: lastJSON.id + 1,
                name: "New Date"
            });
        };

        const newDate = () => {
            const lastDate = state.datesInstances.at(-1);
            state.datesInstances.push({
                id: lastDate.id + 1,
                name: "New Date"
            });
        };

        return {
            ...toRefs(state),
            newJSON,
            newDate
        };
    }
};
</script>
